import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { LinkDirective } from '../../../_shared/directives/link.directive';
import { ICardProduct } from '../../../_shared/models/card/card-product.model';
import { ICardComponent } from '../../../_shared/models/card/card.model';
import { IChip } from '../../../_shared/models/chip.model';
import { IHighlight } from '../../../_shared/models/highlight/highlight.model';
import { ImageData } from '../../../_shared/models/image/image-data.model';
import { EMPTY_LINK, ILink } from '../../../_shared/models/link.model';
import { MediaImage } from '../../../_shared/models/media/media-image.model';
import { IProductProperty } from '../../../_shared/models/product/product-information.model';
import { LinkService } from '../../../_shared/services/link.service';
import { SnowplowService } from '../../../_shared/services/snowplow.service';
import { CardContainer } from '../../../_shared/types/card/card-container.type';
import { CardCatalogueDataSource } from '../../../_shared/types/card-grid-data-source.type';
import { createUrPlusShowroomProduct } from '../../../_shared/types/snowtype/snowplow';
import { withConfigFallback } from '../../../_shared/utils/with-config-fallback';
import { ChipComponent } from '../../../chip/chip.component';
import { HeadingComponent } from '../../../heading/heading.component';
import { HighlightComponent } from '../../../highlight/highlight.component';
import { ImageItemComponent } from '../../../media/media-item/image-item/image-item.component';
import { TextHtmlComponent } from '../../../text-html/text-html.component';

@Component({
    selector: 'sirius-card-product',
    templateUrl: './card-product.component.html',
    styleUrls: ['./card-product.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LinkDirective,
        ImageItemComponent,
        HeadingComponent,
        ChipComponent,
        HighlightComponent,
        TextHtmlComponent,
    ],
    host: {
        // Keeps the card light on dark sections.
        'class': 'is-light',
    },
})
export class CardProductComponent implements ICardComponent<ICardProduct> {
    private _snowplowService = inject(SnowplowService);
    private _linkService = inject(LinkService);

    readonly config = input<ICardProduct | undefined>(undefined);
    readonly imageData = input<ImageData | undefined>(undefined);
    readonly dataSource = input<CardCatalogueDataSource>('other');
    readonly container = input<CardContainer>('slider');

    readonly title = input('');
    /** @deprecated No longer rendered. Retained for downstream template compatibility. */
    readonly brandName = input('');
    readonly teaserHtml = input('');
    readonly image = input<MediaImage | undefined>(undefined);
    readonly tags = input<IChip[]>([]);
    readonly attributes = input<IChip[]>([]);
    readonly properties = input<IProductProperty[]>([]);
    readonly link = input<ILink>(EMPTY_LINK);

    // Internal derived signals read by this component's own template only.
    protected readonly resolvedTitle = withConfigFallback<ICardProduct, 'title', string>(
        this.config,
        'title',
        this.title
    );
    protected readonly resolvedTeaserHtml = withConfigFallback<ICardProduct, 'teaserHtml', string>(
        this.config,
        'teaserHtml',
        this.teaserHtml
    );
    protected readonly resolvedImage = withConfigFallback<ICardProduct, 'image'>(this.config, 'image', this.image);
    protected readonly resolvedTags = withConfigFallback<ICardProduct, 'tags', IChip[]>(this.config, 'tags', this.tags);
    protected readonly resolvedAttributes = withConfigFallback<ICardProduct, 'attributes', IChip[]>(
        this.config,
        'attributes',
        this.attributes
    );
    protected readonly resolvedProperties = withConfigFallback<ICardProduct, 'properties', IProductProperty[]>(
        this.config,
        'properties',
        this.properties
    );
    protected readonly resolvedLink = withConfigFallback<ICardProduct, 'link', ILink>(this.config, 'link', this.link);
    protected readonly resolvedHighlights = computed<IHighlight[]>(() =>
        this.resolvedProperties().map(({ name, value }) => ({ label: name, value }))
    );

    readonly selectEvent = output<void>();

    onClick(): void {
        this.trackLinkClick();
        this.selectEvent.emit();
    }

    private trackLinkClick(): void {
        const link = this.resolvedLink();
        const title = this.resolvedTitle();
        const trackingContext: unknown[] = [];

        if (this.dataSource() === 'urPlus') {
            trackingContext.push(
                createUrPlusShowroomProduct({
                    id: link.tracking?.parameters['product_id'] ?? '',
                    name: title,
                })
            );
        }

        this._snowplowService.track('linkClick', {
            category: 'card',
            container: this.container(),
            identifier: link.tracking?.parameters['identifier'],
            target_url: this._linkService.getFullPath(link.href),
            element_content: link.title,
            outbound: link.target === '_blank',
            context: trackingContext,
        });
    }
}
